import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import {
  addDays,
  differenceInMinutes,
  endOfDay,
  getMonthAndDay,
  isAfter,
  now,
  startOfDay,
} from 'helpers/DateTimeHelper';

import useCurrentPositionAndTime from 'hooks/useCurrentPositionAndTime';

import {
  getEPGHourInterval,
  getLiveDateRange,
  isDayAfter,
  isToday,
  isTomorrow,
  MINUTES_INTERVAL,
} from '../../LiveHelpers';

import Style from './EPGTimeline.module.scss';

const EPGTimeline = props => {
  const {
    accessToken,
    breakpoint,
    coordinates,
    checkAtStarting,
    deviceId,
    endingDate,
    ended,
    fetchLiveAction,
    startingTs,
    totalMinutes,
    selectedDate,
  } = props;
  const dateIsInFuture = isAfter(endOfDay(now()));
  const newDate = dateIsInFuture ? startOfDay(selectedDate) : selectedDate;
  const atStartDate = checkAtStarting(selectedDate);
  const timeLeft = differenceInMinutes(endingDate, now());
  const hourIntervals = getEPGHourInterval(newDate, MINUTES_INTERVAL, Math.ceil(timeLeft / 30));
  const timelineClass = atStartDate ? Style.timelineMainStarting : Style.timelineMain;

  const { currentTime } = useCurrentPositionAndTime({
    totalMinutes,
    startingTs,
    ended,
  });

  const [scrollDate, setScrollDate] = useState(getMonthAndDay(now()));
  const [dateToday, setDateToday] = useState(getMonthAndDay(now()));
  const [dateTomorrow, setDateTomorrow] = useState(getMonthAndDay(addDays(now(), 1)));
  const [dateDayAfter, setDateDayAfter] = useState(getMonthAndDay(addDays(now(), 2)));

  useEffect(() => {
    if (currentTime === '12:00 AM') {
      setScrollDate(getMonthAndDay(now()));
      setDateToday(getMonthAndDay(now()));
      setDateTomorrow(getMonthAndDay(addDays(now(), 1)));
      setDateDayAfter(getMonthAndDay(addDays(now(), 2)));
    }
  }, [currentTime]);

  useEffect(() => {
    const times = document.querySelectorAll('.times');

    const observedTimes = [];

    const dateRange = getLiveDateRange(selectedDate);

    times.forEach(time => {
      if (time.innerHTML === '12:00am' || time.innerHTML === '11:00pm') {
        time.classList.add('observedTimes');
        observedTimes.push(time);
      }
    });

    const handleIntersection = entries => {
      entries.forEach(entry => {
        if (entry.isIntersecting && isToday(entry.target.attributes[1].value)) {
          setScrollDate(dateToday);
        }
        if (entry.isIntersecting && isTomorrow(entry.target.attributes[1].value)) {
          setScrollDate(dateTomorrow);
          fetchLiveAction({ accessToken, dateRange, coordinates, deviceId });
        }
        if (entry.isIntersecting && isDayAfter(entry.target.attributes[1].value)) {
          setScrollDate(dateDayAfter);
          fetchLiveAction({ accessToken, dateRange, coordinates, deviceId });
        }
      });
    };

    const options = {
      root: null,
      rootMargin: '0px',
      threshold: 1,
    };

    const observer = new IntersectionObserver(handleIntersection, options);

    observedTimes.forEach(time => {
      if (window.innerWidth < 768) {
        observer.observe(time);
      } else observer.unobserve(time);
    });

    return () => {
      observer.disconnect();
    };
  }, [breakpoint, dateToday, dateTomorrow, dateDayAfter]);

  const intervalElement = hourIntervals.map((obj, idx) => (
    <div
      className={classNames(Style.time, 'times')}
      key={`timeline-${obj.dateTimeString}`}
      data-date={obj.current}
    >
      {obj.timeString}
    </div>
  ));

  return (
    <div className={Style.timelineWrap}>
      <div className={classNames(Style.scrollDate, 'date')}>{scrollDate}</div>
      <div id="timeline-main" className={timelineClass}>
        {intervalElement}
      </div>
    </div>
  );
};

EPGTimeline.propTypes = {
  accessToken: PropTypes.string,
  breakpoint: PropTypes.string,
  checkAtStarting: PropTypes.func,
  coordinates: PropTypes.shape({}),
  deviceId: PropTypes.string,
  ended: PropTypes.func,
  endingDate: PropTypes.instanceOf(Date),
  fetchLiveAction: PropTypes.func,
  startingTs: PropTypes.number.isRequired,
  selectedDate: PropTypes.instanceOf(Date),
  totalMinutes: PropTypes.number.isRequired,
};

export default EPGTimeline;
